import { useState } from 'react';
import {
  Body,
  Card,
  Eyebrow,
  PageHeader,
  Stack,
  useDelayedLoading,
} from '@leanin/ui';
import { SaveBar } from '../../form/SaveBar';
import {
  AdminAccessPolicyChoice,
  type AdminAccess,
} from './AdminAccessPolicyChoice';
import {
  AdminJoinPolicyChoice,
  type AdminJoinPolicy,
} from './AdminJoinPolicyChoice';
import { AdminFormPageSkeleton } from './AdminFormPageSkeleton';
import { useCircle, useNetwork } from '../../../lib/queries/groups';
import {
  useUpdateCircle,
  useUpdateNetwork,
} from '../../../lib/mutations/groups';
import { useSpace } from '../../../lib/queries/spaces';
import { useUpdateSpace } from '../../../lib/mutations/spaces';

// Composes the two access-axis primitives into the canonical "Visibility &
// access" tab body. Circles, Networks, and Spaces share this — only the
// kind label and the underlying mutation differ. Topics, capacity,
// location, etc. each live on the kind's Overview / Members / Settings
// tab; this surface owns only the two access axes.

export type GroupVisibilityKind = 'circle' | 'network' | 'space';

type Props = {
  readonly id: string;
  readonly kind: GroupVisibilityKind;
};

const KIND_LABEL: Record<GroupVisibilityKind, 'Circle' | 'Network' | 'Space'> =
  {
    circle: 'Circle',
    network: 'Network',
    space: 'Space',
  };

// Access options hidden per kind. Circles and Networks never expose
// `unlisted` — a directory-listed product surface shouldn't offer a
// reachable-by-link-but-hidden mode the rest of the copy doesn't explain.
const ACCESS_OMIT: Record<GroupVisibilityKind, ReadonlyArray<AdminAccess>> = {
  circle: ['unlisted'],
  network: ['unlisted'],
  space: [],
};

// Kinds whose join axis is fixed rather than chosen. Every Network is
// request-to-join: there is no invite-only path, so the join section renders
// a statement, and saves funnel the row to `'request'` (normalizing legacy
// rows on next save) rather than offering a picker.
const JOIN_LOCKED: Record<GroupVisibilityKind, boolean> = {
  circle: false,
  network: true,
  space: false,
};

// Kinds where the join policy is not surfaced at all. Groups (spaces) are
// always open — anyone can join — so showing a join section would be
// misleading. The underlying joinPolicy field is preserved but not editable.
const JOIN_HIDDEN: Record<GroupVisibilityKind, boolean> = {
  circle: false,
  network: false,
  space: true,
};

type Loaded = {
  readonly access: AdminAccess;
  readonly joinPolicy: AdminJoinPolicy;
  readonly save: (next: {
    access: AdminAccess;
    joinPolicy: AdminJoinPolicy;
  }) => Promise<void>;
  readonly isPending: boolean;
};

// One inner component per kind so each only mounts the hooks it actually
// needs. The outer component picks which inner to render; the inner runs
// its own fetch + mutation, normalizes the result into `Loaded`, and hands
// off to the shared form body.

export function GroupVisibilitySection({ id, kind }: Props) {
  if (kind === 'circle') return <CircleInner id={id} />;
  if (kind === 'network') return <NetworkInner id={id} />;
  return <SpaceInner id={id} />;
}

function CircleInner({ id }: { readonly id: string }) {
  const detailQuery = useCircle(id);
  const mutation = useUpdateCircle();
  const detail = detailQuery.data;
  const showSkeleton = useDelayedLoading(
    detail === undefined || detail.access === 'visitor',
  );
  if (showSkeleton) {
    return <AdminFormPageSkeleton cards={1} rowsPerCard={3} withAction />;
  }
  if (detail === undefined || detail.access === 'visitor') return null;
  const circle = detail.circle;
  const loaded: Loaded = {
    access: circle.access,
    joinPolicy: circle.joinPolicy,
    isPending: mutation.isPending,
    save: async ({ access, joinPolicy }) => {
      await mutation.mutateAsync({
        circleId: circle.id,
        access,
        joinPolicy,
        expectedVersion: circle.version,
      });
    },
  };
  return <VisibilityForm kind="circle" loaded={loaded} />;
}

function NetworkInner({ id }: { readonly id: string }) {
  const detailQuery = useNetwork(id);
  const mutation = useUpdateNetwork();
  const network = detailQuery.data;
  const showSkeleton = useDelayedLoading(network === undefined);
  if (showSkeleton) {
    return <AdminFormPageSkeleton cards={1} rowsPerCard={3} withAction />;
  }
  if (network === undefined) return null;
  const loaded: Loaded = {
    access: network.access,
    // Networks are always request-to-join (see JOIN_LOCKED) — the join axis
    // isn't editable here and the network PATCH contract doesn't accept it,
    // so saves carry `access` only.
    joinPolicy: 'request',
    isPending: mutation.isPending,
    save: async ({ access }) => {
      await mutation.mutateAsync({
        networkId: network.id,
        access,
      });
    },
  };
  return <VisibilityForm kind="network" loaded={loaded} />;
}

function SpaceInner({ id }: { readonly id: string }) {
  const detailQuery = useSpace(id);
  const mutation = useUpdateSpace();
  const space = detailQuery.data ?? null;
  const showSkeleton = useDelayedLoading(space === null);
  if (showSkeleton) {
    return <AdminFormPageSkeleton cards={1} rowsPerCard={3} withAction />;
  }
  if (space === null) return null;
  const loaded: Loaded = {
    access: space.access,
    joinPolicy: space.joinPolicy,
    isPending: mutation.isPending,
    save: async ({ access }) => {
      await mutation.mutateAsync({ id: space.id, access });
    },
  };
  return <VisibilityForm kind="space" loaded={loaded} />;
}

function VisibilityForm({
  kind,
  loaded,
}: {
  readonly kind: GroupVisibilityKind;
  readonly loaded: Loaded;
}) {
  const kindLabel = KIND_LABEL[kind];
  const joinLocked = JOIN_LOCKED[kind];
  const joinHidden = JOIN_HIDDEN[kind];
  const [access, setAccess] = useState<AdminAccess>(loaded.access);
  const [joinPolicy, setJoinPolicy] = useState<AdminJoinPolicy>(
    joinLocked ? 'request' : loaded.joinPolicy,
  );
  const [error, setError] = useState<string | null>(null);

  const dirty =
    access !== loaded.access ||
    (!joinLocked && !joinHidden && joinPolicy !== loaded.joinPolicy);

  const handleSave = async () => {
    if (!dirty) return;
    setError(null);
    try {
      await loaded.save({ access, joinPolicy });
    } catch (err) {
      setError(
        err instanceof Error
          ? err.message
          : `Failed to update ${kindLabel} visibility.`,
      );
    }
  };

  return (
    <Stack gap="lg">
      <PageHeader title="Visibility & access" />

      <Card pad="default">
        <Stack gap="xl">
          <AdminAccessPolicyChoice
            value={access}
            onChange={setAccess}
            entityLabel={kindLabel}
            omit={ACCESS_OMIT[kind]}
          />

          {!joinHidden && (joinLocked ? (
            <>
              <Stack gap="md">
                <Eyebrow tone="accent">How members join</Eyebrow>
                <Body size="sm" color="mute">
                  {`Every ${kindLabel} is request-to-join — members ask to join and a leader approves each request before they can see member-only content.`}
                </Body>
              </Stack>

              <Stack gap="md">
                <Eyebrow tone="accent">{`Who can see this ${kindLabel}'s content?`}</Eyebrow>
                <Body size="sm" color="mute">
                  {`Feed, resources, and events can only be viewed by members of the ${kindLabel}.`}
                </Body>
              </Stack>
            </>
          ) : (
            <AdminJoinPolicyChoice
              value={joinPolicy}
              onChange={setJoinPolicy}
              entityLabel={kindLabel}
            />
          ))}
        </Stack>
      </Card>

      <SaveBar
        dirty={dirty}
        saving={loaded.isPending}
        error={error}
        onSave={handleSave}
      />
    </Stack>
  );
}
